import { useEffect, useState, useRef } from "react";
import { db } from "./firebase";
import { collection, onSnapshot, doc, updateDoc } from "firebase/firestore";
import confetti from "canvas-confetti";

function App() {
  const [candidatos, setCandidatos] = useState([]);
  const [chat, setChat] = useState([]);
  const [alerta, setAlerta] = useState("");
  const [reacciones, setReacciones] = useState([]);

  const ultimoGanador = useRef(null);
  const userInteracted = useRef(false);

  // 🔥 activar audio (evita error navegador)
  useEffect(() => {
    const activar = () => {
      userInteracted.current = true;
      window.removeEventListener("click", activar);
    };
    window.addEventListener("click", activar);
  }, []);

  // 🔥 firebase realtime
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "candidatos"), (snapshot) => {
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));

      setCandidatos(data);
      detectarGanador(data);
    });

    return () => unsubscribe();
  }, []);

  // 🔥 detectar ganador
  const detectarGanador = (data) => {
    if (data.length === 0) return;

    const max = Math.max(...data.map((c) => c.votos));
    const ganador = data.find((c) => c.votos === max);

    if (!ganador) return;

    if (ultimoGanador.current !== ganador.id) {
      ultimoGanador.current = ganador.id;

      setAlerta(`🔥 ${ganador.nombre} va ganando`);

      lanzarConfetti();
      sonidoGanador();
      hablar(`${ganador.nombre} va ganando`);
      agregarChat("Sistema", `${ganador.nombre} lidera 🔥`);
      lanzarReaccion();

      setTimeout(() => setAlerta(""), 3000);
    }
  };

  // 🎉 confetti
  const lanzarConfetti = () => {
    confetti({
      particleCount: 120,
      spread: 80,
      origin: { y: 0.6 }
    });
  };

  // 🔊 sonidos seguros
  const sonidoClick = () => {
    if (!userInteracted.current) return;
    const audio = new Audio("https://actions.google.com/sounds/v1/cartoon/pop.ogg");
    audio.play().catch(() => {});
  };

  const sonidoGanador = () => {
    if (!userInteracted.current) return;
    const audio = new Audio("https://actions.google.com/sounds/v1/crowds/cheer.ogg");
    audio.play().catch(() => {});
  };

  // 🗣 voz
  const hablar = (texto) => {
    if (!userInteracted.current) return;
    const msg = new SpeechSynthesisUtterance(texto);
    msg.lang = "es-ES";
    speechSynthesis.speak(msg);
  };

  // 🗳 votar
  const votar = async (id) => {
    sonidoClick();

    const ref = doc(db, "candidatos", id);
    const candidato = candidatos.find((c) => c.id === id);

    if (!candidato) return;

    await updateDoc(ref, {
      votos: candidato.votos + 1
    });

    agregarChat("Usuario", "🔥 votó");
    lanzarReaccion();
  };

  // 💬 chat estilo tiktok
  const agregarChat = (user, text) => {
    setChat((prev) => [...prev.slice(-20), { user, text }]);
  };

  // ❤️ reacciones flotantes
  const lanzarReaccion = () => {
    const emojis = ["🔥", "❤️", "👏", "👍"];
    const emoji = emojis[Math.floor(Math.random() * emojis.length)];

    const id = Date.now();

    setReacciones((prev) => [...prev, { id, emoji }]);

    setTimeout(() => {
      setReacciones((prev) => prev.filter((r) => r.id !== id));
    }, 2000);
  };

  // 📊 ordenar SIN ERROR
  const candidatosOrdenados = [...candidatos].sort(
    (a, b) => b.votos - a.votos
  );

  const total = candidatos.reduce((a, b) => a + b.votos, 0);

  return (
    <div style={styles.container}>
      {/* 🔥 HEADER PRO */}
      <div style={styles.header}>
        <h1 style={styles.title}>🔥 ELECCIONES EN VIVO 🔥</h1>
        <div style={styles.total}>👥 {total} votos</div>
      </div>

      {alerta && <div style={styles.alerta}>{alerta}</div>}

      <div style={styles.main}>
        {/* 🏆 CANDIDATOS */}
        <div style={styles.grid}>
          {candidatosOrdenados.map((c, i) => {
            const porcentaje =
              total > 0 ? ((c.votos / total) * 100).toFixed(1) : 0;

            return (
              <div key={c.id} style={styles.card}>
                <h2>{i + 1}° {c.nombre}</h2>
                <p>{c.partido}</p>

                <h1 style={styles.votos}>{c.votos}</h1>
                <p>{porcentaje}%</p>

                <button style={styles.button} onClick={() => votar(c.id)}>
                  🔥 VOTAR
                </button>
              </div>
            );
          })}
        </div>

        {/* 💬 CHAT PRO */}
        <div style={styles.chat}>
          <h3>💬 CHAT EN VIVO</h3>

          <div style={styles.chatList}>
            {chat.map((c, i) => (
              <div key={i} style={styles.chatItem}>
                <b>{c.user}:</b> {c.text}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ❤️ REACCIONES */}
      <div style={styles.reacciones}>
        {reacciones.map((r) => (
          <div key={r.id} style={styles.reaccion}>
            {r.emoji}
          </div>
        ))}
      </div>
    </div>
  );
}

export default App;

const styles = {
  container: {
    background: "#020617",
    color: "white",
    minHeight: "100vh",
    padding: "20px"
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },

  title: {
    fontSize: "28px"
  },

  total: {
    fontSize: "22px",
    color: "#22c55e"
  },

  alerta: {
    background: "gold",
    color: "black",
    padding: "10px",
    textAlign: "center",
    borderRadius: "10px",
    margin: "10px 0"
  },

  main: {
    display: "flex",
    gap: "20px"
  },

  grid: {
    flex: 3,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "20px"
  },

  card: {
    background: "#1e293b",
    padding: "20px",
    borderRadius: "12px",
    textAlign: "center"
  },

  votos: {
    fontSize: "32px",
    color: "#22c55e"
  },

  button: {
    marginTop: "10px",
    padding: "10px",
    borderRadius: "10px",
    border: "none",
    background: "#22c55e",
    color: "black",
    cursor: "pointer"
  },

  chat: {
    flex: 1,
    background: "rgba(255,255,255,0.05)",
    padding: "10px",
    borderRadius: "10px",
    height: "80vh"
  },

  chatList: {
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: "5px"
  },

  chatItem: {
    background: "rgba(255,255,255,0.1)",
    padding: "8px",
    borderRadius: "8px"
  },

  reacciones: {
    position: "fixed",
    right: "20px",
    bottom: "20px"
  },

  reaccion: {
    fontSize: "24px",
    animation: "float 2s ease-out"
  }
};